import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Chip, Container, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { bonusPortalAPI, bonusTrackerAPI } from '../services/api';
import TeksSolutionsList from '../components/bonuses/TeksSolutionsList';
import AddBonusDialog from '../components/bonuses/AddBonusDialog';
import BonusDetailDialog from '../components/bonuses/BonusDetailDialog';
import { BonusCategory, BonusSummary, Campaign, STAGE_LABEL, Stage, errText, money } from '../components/bonuses/bonusTypes';

/** Minimal CSV reader (quotes and commas inside quotes). The first line is the header. */
function parseCsv(text: string): Array<Record<string, string>> {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i += 1; } else if (ch === '"') quoted = false; else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell); cell = '';
      if (row.some((c) => c.trim())) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim())) rows.push(row);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  return rows.slice(1).map((r) => Object.fromEntries(header.map((h, idx) => [h, (r[idx] || '').trim()])));
}

const STATUS_COLOR: Record<string, 'warning' | 'success' | 'error'> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'error' };
const STATUS_LABEL: Record<string, string> = { PENDING: 'In approval', APPROVED: 'Approved', REJECTED: 'Rejected' };
const FREQ_LABEL: Record<string, string> = { WEEKLY: 'Weekly', MONTHLY: 'Monthly', ONE_TIME: 'One time', PER_EVENT: 'Per event' };

const StageChip: React.FC<{ stage: BonusSummary['stages'][number]; waiting: boolean }> = ({ stage, waiting }) => {
  const done = stage.decision;
  const color = done === 'Y' ? 'success' : done === 'N' ? 'error' : waiting ? 'warning' : 'default';
  const isPresident = stage.stage === 'PRESIDENT';
  const detail = done
    ? isPresident ? (done === 'Y' ? 'Approved' : 'Rejected') : `${done === 'Y' ? 'Approved' : 'Rejected'} · ${stage.byName || 'unknown'} · ${stage.date || ''}${stage.note ? ` · ${stage.note}` : ''}`
    : waiting ? 'Waiting' : 'Not yet';
  return (
    <Tooltip title={detail}>
      <Chip size="small" color={color as any} variant={done ? 'filled' : 'outlined'} label={STAGE_LABEL[stage.stage as Stage]} />
    </Tooltip>
  );
};

const BonusTrackerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // The TEKS Solutions tab is a read-only list from TOGO OPS; a user may have only that one.
  const [portalAllowed, setPortalAllowed] = useState<boolean | null>(null);
  const [me, setMe] = useState<{ allowed: boolean; canEnter: boolean; stages: Stage[] } | null>(null);
  const [categories, setCategories] = useState<BonusCategory[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [records, setRecords] = useState<BonusSummary[]>([]);
  const [filters, setFilters] = useState({ categoryId: '', frequency: '', campaignId: '', status: '', search: '', included: '', from: '', to: '' });
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const params = useMemo(() => {
    const out: Record<string, string | undefined> = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) out[k] = v; });
    return out;
  }, [filters]);

  const load = useCallback(async () => {
    try {
      setRecords(await bonusTrackerAPI.records(params));
    } catch (e) {
      setMessage({ type: 'error', text: errText(e) });
    }
  }, [params]);

  useEffect(() => {
    (async () => {
      try {
        const access = await bonusTrackerAPI.me();
        setMe(access);
        if (access.allowed) {
          setCategories(await bonusTrackerAPI.catalog());
          setCampaigns(await bonusTrackerAPI.campaigns());
        }
      } catch (e) {
        setMessage({ type: 'error', text: errText(e) });
      }
    })();
    bonusPortalAPI.me().then((portal: { allowed?: boolean }) => setPortalAllowed(Boolean(portal?.allowed))).catch(() => setPortalAllowed(false));
  }, []);
  useEffect(() => { if (me?.allowed) void load(); }, [load, me]);

  const trackerAllowed = Boolean(me?.allowed);
  // The TEKS Solutions tab is hidden for now (kept in the code); flip this to show it again.
  const showTeks = false;
  const activeTab: 'bonuses' | 'teks' = searchParams.get('tab') === 'teks' && showTeks ? 'teks' : trackerAllowed ? 'bonuses' : 'teks';

  const importCsv = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = parseCsv(await file.text());
      if (!parsed.length) {
        setMessage({ type: 'error', text: 'The file has no rows. The first line must be the header: category,employee,period,amount,frequency,campaign,currency,concept,reference.' });
        return;
      }
      const result = await bonusTrackerAPI.importRows(parsed.map((r) => ({
        category: r.category, employee: r.employee, period: r.period, frequency: r.frequency || undefined, campaign: r.campaign || null,
        amount: r.amount, currency: r.currency || undefined, concept: r.concept || null, reference: r.reference || null,
      })));
      const skipped = result.skipped.length ? ` ${result.skipped.length} row(s) skipped: ${result.skipped.slice(0, 5).map((s) => `row ${s.row}: ${s.reason}`).join('; ')}${result.skipped.length > 5 ? '…' : ''}` : '';
      setMessage({ type: result.employees ? 'success' : 'error', text: `${result.employees} employee(s) imported into ${result.records} bonus(es) for approval.${skipped}` });
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: errText(e) });
    }
  };

  const exportCsv = async () => {
    try {
      const blob = await bonusTrackerAPI.exportCsv(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bonuses-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMessage({ type: 'error', text: errText(e) });
    }
  };

  const set = (key: keyof typeof filters) => (value: string) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Typography variant="h4" gutterBottom>Bonus Tracker</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add the employees that qualify for a bonus, then follow it through the approvals. Every approval records who did it and when.
      </Typography>
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {me && !trackerAllowed && portalAllowed === false && (
        <Alert severity="info" sx={{ mb: 2 }}>Bonuses are available to supervisors and above.</Alert>
      )}

      <Tabs value={activeTab} onChange={(_e, v) => setSearchParams(v === 'teks' ? { tab: 'teks' } : {})} sx={{ mb: 2 }}>
        {trackerAllowed && <Tab value="bonuses" label="Bonuses" />}
        {showTeks && <Tab value="teks" label="TEKS Solutions" />}
      </Tabs>

      {activeTab === 'bonuses' && trackerAllowed && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Category</InputLabel>
                <Select label="Category" value={filters.categoryId} onChange={(e) => set('categoryId')(String(e.target.value))}>
                  <MenuItem value="">All</MenuItem>
                  {categories.filter((c) => !c.isSystem).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Frequency</InputLabel>
                <Select label="Frequency" value={filters.frequency} onChange={(e) => set('frequency')(String(e.target.value))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="WEEKLY">Weekly</MenuItem>
                  <MenuItem value="MONTHLY">Monthly</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Campaign</InputLabel>
                <Select label="Campaign" value={filters.campaignId} onChange={(e) => set('campaignId')(String(e.target.value))}>
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="ALL">All campaigns</MenuItem>
                  {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={filters.status} onChange={(e) => set('status')(String(e.target.value))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PENDING">In approval</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>In payroll</InputLabel>
                <Select label="In payroll" value={filters.included} onChange={(e) => set('included')(String(e.target.value))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="no">Not yet paid</MenuItem>
                  <MenuItem value="yes">Already in payroll</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" type="date" label="Period ends from" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => set('from')(e.target.value)} />
              <TextField size="small" type="date" label="to" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => set('to')(e.target.value)} />
              <TextField size="small" label="Search employee" value={filters.search} onChange={(e) => set('search')(e.target.value)} />
              <Button onClick={exportCsv}>Export CSV</Button>
              {me?.canEnter && (
                <Tooltip title="CSV columns: category, employee (number, e-mail or full name), period (last day, YYYY-MM-DD), amount, frequency, campaign, currency, concept, reference. Imported bonuses still need every approval.">
                  <Button component="label">
                    Import CSV
                    <input hidden type="file" accept=".csv,text/csv" onChange={(e) => { void importCsv(e.target.files?.[0]); e.target.value = ''; }} />
                  </Button>
                </Tooltip>
              )}
              {me?.canEnter && <Button variant="contained" onClick={() => setAdding(true)}>Add bonus</Button>}
            </Stack>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Period ends</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell align="right">Employees</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Approvals</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payroll</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenId(r.id)}>
                    <TableCell>
                      {r.categoryName}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {FREQ_LABEL[r.frequency] || r.frequency} · {r.offeredBy === 'CUSTOMER' ? 'paid by the customer' : 'paid in house'}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.periodEnd}</TableCell>
                    <TableCell>{r.campaignName}</TableCell>
                    <TableCell align="right">{r.employeeCount}</TableCell>
                    <TableCell align="right">{money(r.total, r.currency)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {r.stages.map((s) => <StageChip key={s.stage} stage={s} waiting={r.nextStage === s.stage} />)}
                      </Stack>
                    </TableCell>
                    <TableCell><Chip size="small" color={STATUS_COLOR[r.status]} label={STATUS_LABEL[r.status]} /></TableCell>
                    <TableCell>
                      {r.includedInPayroll ? <Chip size="small" color="info" label={r.allIncluded ? 'In payroll' : 'Partly in payroll'} /> : <Typography variant="caption" color="text.secondary">Not paid</Typography>}
                      {r.hrSynced && <Chip size="small" sx={{ ml: 0.5 }} label="HR synced" />}
                    </TableCell>
                    <TableCell align="right"><Button size="small" onClick={(e) => { e.stopPropagation(); setOpenId(r.id); }}>Open</Button></TableCell>
                  </TableRow>
                ))}
                {!records.length && (
                  <TableRow><TableCell colSpan={9}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No bonuses match these filters.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {activeTab === 'teks' && showTeks && <TeksSolutionsList />}

      {adding && (
        <AddBonusDialog
          categories={categories.filter((c) => !c.isSystem)}
          campaigns={campaigns}
          onClose={() => setAdding(false)}
          onCreated={(id, text) => { setAdding(false); setMessage({ type: 'success', text }); void load(); setOpenId(id); }}
          onOpenExisting={(id) => { setAdding(false); setOpenId(id); }}
        />
      )}
      {openId && (
        <BonusDetailDialog
          id={openId}
          categories={categories.filter((c) => !c.isSystem)}
          campaigns={campaigns}
          onClose={() => setOpenId(null)}
          onChanged={() => { void load(); }}
        />
      )}
    </Container>
  );
};

export default BonusTrackerPage;
